// Accommodation demand vs supply chart + forward-booking task list.
import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { Spinner, ErrorBox, Modal } from '../ui';
import { toAus, todayDarwin, addMonths } from '../util';

export default function Demand() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [deadlines, setDeadlines] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestText, setRequestText] = useState<string | null>(null);
  const from = todayDarwin();
  const to = addMonths(from, 6);

  useEffect(() => {
    Promise.all([api(`/demand?from=${from}&to=${to}`), api('/deadlines')])
      .then(([d, dl]) => { setRows(d); setDeadlines(dl); })
      .catch(e => setError(e.message));
  }, []);

  if (error) return <ErrorBox error={error} />;
  if (!rows || !deadlines) return <Spinner label="Calculating demand…" />;

  const max = Math.max(1, ...rows.map(r => Math.max(r.required, r.booked)));

  return (
    <div className="page">
      <div className="page-head"><h2>Accommodation demand &amp; booking deadlines</h2></div>

      <div className="panel">
        <h3>Rooms required vs booked per night — next six months</h3>
        <div className="legend">
          <span style={{ '--c': 'var(--olive)' } as any}>Confirmed demand</span>
          <span style={{ '--c': '#c9a227' } as any}>Tentative demand</span>
          <span style={{ '--c': '#3e6b4f' } as any}>Booked (line)</span>
          <span style={{ '--c': 'var(--danger)' } as any}>Shortfall night</span>
        </div>
        <div className="chart-wrap">
          <div className="bar-chart" style={{ minWidth: rows.length * 5 }}>
            {rows.map(r => (
              <div key={r.date} className="bar-col"
                title={`${toAus(r.date)}\nRequired: ${r.required} (tentative ${r.tentative_demand})\nBooked: ${r.booked} (confirmed ${r.confirmed_booked})\nUnallocated: ${r.unallocated}\n${r.shortfall ? 'SHORT ' + r.shortfall : r.surplus ? 'Surplus ' + r.surplus : 'Balanced'}`}>
                {/* booked marker */}
                <div style={{ position: 'absolute', bottom: `${(r.booked / max) * 100}%`, left: 0, right: 0, borderTop: '2px solid #3e6b4f', zIndex: 2 }} />
                {/* demand stack */}
                <div style={{ height: `${(r.tentative_demand / max) * 100}%`, background: '#c9a227' }} />
                <div style={{ height: `${(r.confirmed_demand / max) * 100}%`, background: r.shortfall > 0 ? 'var(--danger)' : 'var(--olive)' }} />
              </div>
            ))}
          </div>
        </div>
        <p className="muted">Hover any night for detail. Max scale: {max} rooms. A person checking out does not occupy a room that night.</p>
      </div>

      <div className="panel">
        <h3>Booking actions ({deadlines.tasks.length})</h3>
        <p className="muted">
          Lead times from project settings: book {deadlines.settings?.booking_lead_days} days out (prefer {deadlines.settings?.preferred_lead_days}),
          names due {deadlines.settings?.names_due_lead_days} days out, cancellation notice {deadlines.settings?.cancellation_notice_days} days,
          contingency {deadlines.settings?.contingency_percent}%, minimum block {deadlines.settings?.min_block_size}.
          Change these in <a href="#/settings">Settings</a>.
        </p>
        {deadlines.tasks.length === 0 && <p className="muted">No shortfalls — every forecast night is covered by bookings.</p>}
        <div className="table-wrap" style={{ boxShadow: 'none' }}>
          <div className="table-scroll">
            <table>
              <thead>
                <tr><th>Action</th><th>Nights</th><th>Recommended by</th><th>Book by (latest safe)</th><th>Names due</th><th>Cancel deadline</th><th>Contingency</th><th></th></tr>
              </thead>
              <tbody>
                {deadlines.tasks.map((t: any, i: number) => (
                  <tr key={i} style={t.overdue ? { background: 'color-mix(in srgb, var(--danger) 12%, transparent)' } : undefined}>
                    <td><strong>Book {t.rooms_to_book} room(s)</strong> for {toAus(t.check_in)} – {toAus(t.check_out)}{t.overdue ? ' — OVERDUE' : t.urgent ? ' — due soon' : ''}</td>
                    <td>{t.nights}</td>
                    <td>{toAus(t.recommended_booking_date)}</td>
                    <td><strong>{toAus(t.latest_safe_booking_date)}</strong></td>
                    <td>{toAus(t.names_due_date)}</td>
                    <td>{toAus(t.cancellation_deadline)}</td>
                    <td>+{t.recommended_contingency}</td>
                    <td><button className="btn btn-sm" onClick={() => setRequestText(t.request_text)}>Booking request text</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {requestText && (
        <Modal title="Booking request — copy into an email" onClose={() => setRequestText(null)} wide>
          <textarea className="input" rows={16} readOnly value={requestText} style={{ fontFamily: 'Consolas, monospace' }} />
          <div className="btn-row form-actions">
            <button className="btn btn-primary" onClick={() => { navigator.clipboard.writeText(requestText); }}>Copy to clipboard</button>
            <button className="btn" onClick={() => setRequestText(null)}>Close</button>
          </div>
          <p className="muted">This app does not send emails — paste this into your email client and review before sending.</p>
        </Modal>
      )}
    </div>
  );
}
